import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { useAuthStore, SAVED_ID_KEY } from '../stores/auth';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // 이미 인증된 상태면 홈으로 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 저장된 아이디 불러오기
  useEffect(() => {
    const savedId = localStorage.getItem(SAVED_ID_KEY);
    if (savedId) {
      setIdentifier(savedId);
      setRememberMe(true);
    }
  }, []);

  // 로그인 후 리다이렉트할 경로 (query param 우선, 그 다음 state, 기본값 /)
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const from = redirectParam || (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    // 유효성 검사
    if (!identifier.trim()) {
      setLocalError('이메일 또는 아이디를 입력해주세요.');
      return;
    }
    if (!password) {
      setLocalError('비밀번호를 입력해주세요.');
      return;
    }

    try {
      // 아이디 저장 처리
      if (rememberMe) {
        localStorage.setItem(SAVED_ID_KEY, identifier.trim());
      } else {
        localStorage.removeItem(SAVED_ID_KEY);
      }

      const result = await login({ identifier: identifier.trim(), password });

      // 탈퇴 유예 기간 내 계정 — store에 restorePending 저장됨, App 레벨 RestoreConfirmHost가 모달 표시
      if ('restorePending' in result && result.restorePending) {
        return;
      }

      const { organizationRestored } = result as { accountRestored: boolean; organizationRestored: boolean };

      // 조직 복원 실패 시 대시보드로 이동 (이전 페이지에 접근 권한이 없을 수 있음)
      if (!organizationRestored && from !== '/') {
        navigate('/', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch {
      // 에러는 store에서 처리
    }
  };

  const displayError = localError || error;

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>
          <h2 className="mt-4 text-2xl font-bold text-ink-900">로그인</h2>
          <p className="mt-2 text-sm text-ink-600">
            계정이 없으신가요?{' '}
            <Link to={redirectParam ? `/signup?redirect=${encodeURIComponent(redirectParam)}` : '/signup'} className="font-medium text-brand-600 hover:text-brand-500">
              회원가입
            </Link>
          </p>
        </div>

        {/* 에러 메시지 */}
        {displayError && (
          <div className="rounded-card bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 whitespace-pre-line">{displayError}</p>
              </div>
            </div>
          </div>
        )}

        {/* 로그인 폼 */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* 이메일 또는 아이디 */}
            <div>
              <label htmlFor="identifier" className="block text-sm font-medium text-ink-700">
                이메일 또는 아이디
              </label>
              <input
                id="identifier"
                name="identifier"
                type="text"
                autoComplete="username"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm placeholder-ink-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                placeholder="이메일 또는 아이디 입력"
              />
            </div>

            {/* 비밀번호 */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink-700">
                비밀번호
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-ink-300 rounded-control shadow-sm placeholder-ink-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
                placeholder="********"
              />
            </div>
          </div>

          {/* 아이디 저장 & 비밀번호 찾기 */}
          <div className="flex items-center justify-between">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-ink-300 rounded"
              />
              <span className="ml-2 text-sm text-ink-600">아이디 저장</span>
            </label>
            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-brand-600 hover:text-brand-500"
              >
                비밀번호를 잊으셨나요?
              </Link>
            </div>
          </div>

          {/* 로그인 버튼 */}
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  로그인 중...
                </>
              ) : (
                '로그인'
              )}
            </Button>
          </div>
        </form>

        {/* 푸터 */}
        <p className="mt-8 text-center text-xs text-ink-500">
          SafetyHub &copy; 2025. All rights reserved.
        </p>
      </div>

    </div>
  );
}
